import { action, makeObservable, observable } from "mobx";
import { AdminImportsStore, ImportDataType, ImportJobModel } from "../admin.imports.store";
import { UiStore } from "../ui.store";
import { IAdminUploadOptions } from "app/components/admin/AdminFileUpload";
import moment from "moment";
import { notify } from "app/components/common/notify";
import { DatenStandType } from "../datenstand.store";


export const isDvsImportFileName = (fileName: string, dataType: ImportDataType): boolean => {
    const regex = new RegExp(`^${dataType}_\\d{8}(_\\w+)?_?.*\\.csv$`);
    return regex.test(fileName);
}

export const extractDatenstandFromFileName = (fileName: string): Date | null => {
    const match = fileName.match(/_(\d{8})(?:.|\.csv)/);
    if (match) {
        let m = moment(match[1], 'YYYYMMDD', true);
        if (!m.isValid()) {
            // e.g. leadnowconv delivery: KW34_23082026.csv (DDMMYYYY)
            m = moment(match[1], 'DDMMYYYY', true);
        }
        return m.isValid() ? m.toDate() : null;
    }
    return null;
}

// Extracts a number (KW) in square brackets before .csv, e.g. ..._[202401]_...csv
// Falls back to a "KW34"-style prefix combined with the date in the filename,
// e.g. KW34_23082026.csv -> 202634
export const extractKWFromFileName = (fileName: string): number | null => {
    const match = fileName.match(/\[(\d+)\]/);
    if (match) {
        return parseInt(match[1], 10);
    }
    const kwMatch = fileName.match(/KW(\d{1,2})/i);
    if (kwMatch) {
        const stand = extractDatenstandFromFileName(fileName);
        if (stand) {
            return moment(stand).isoWeekYear() * 100 + parseInt(kwMatch[1], 10);
        }
    }
    return null;
}

export class AdminFileUploadUiStore {

    uiStore: UiStore;
    adminImportsStore: AdminImportsStore

    constructor(uiStore: UiStore, adminImportsStore: AdminImportsStore) {
        makeObservable(this);
        this.uiStore = uiStore;
        this.adminImportsStore = adminImportsStore;
    }


    @observable
    newFilename?: string;

    @observable
    orgFilename?: string;
    onFileUploaded?: () => void;

    @observable
    orgUploadDate?: Date | null;


    @action
    _newUpload(filename: string) {
        this.orgFilename = filename;
        this.newFilename = undefined;
        this.onFileUploaded = undefined;
        this.orgUploadDate = undefined;
    }



    @action
    async uploadFile(file: any, opts: IAdminUploadOptions, onProgres: (p: number) => void) {
        if (opts.dataType && ![ImportDataType.aup, ImportDataType.aupvorjahr].includes(opts.dataType)) {
            this._newUpload(file.path);

            this.orgUploadDate = this.extractDatenstandFromFileName(file.path);


            this.uiStore.showModal(this.uiStore.modalIds.adminFileUploadRenamFile);
            while (this.newFilename === undefined) {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
            this.uiStore.hideModal(this.uiStore.modalIds.adminFileUploadRenamFile);
            if (this.newFilename === 'cancel') {
                this.uiStore.setIsUploading(false);
                return;
            }
            opts.fileName = this.newFilename;
        }
        await this.adminImportsStore.uploadFile(file, opts, onProgres);
        if (this.onFileUploaded) {
            this.onFileUploaded();
        }

    }

    calcNewFileName(stand: Date, dst: DatenStandType, zusatz?: string, kw?: number): string {

        const d = moment(stand).format('YYYYMMDD');
        const z = zusatz ? '_' + zusatz : '';
        let kwPart = kw ? `_[${kw}]` : '';
        if (!dst.requireKwInput) {
            kwPart = ``;
        }

        return dst.dataType + '_' + d + z + kwPart + '.csv'
    }
    isDvsImportFileName(fileName: string, dataType: ImportDataType): boolean {
        return isDvsImportFileName(fileName, dataType);
    }
    extractDatenstandFromFileName(fileName: string): Date | null {
        return extractDatenstandFromFileName(fileName);
    }

    prepareJobForTrigger(fileName: string, job: ImportJobModel) {
        if (!job.meta) {
            job.meta = {};
        }
        job.meta.fileName = fileName;
        job.meta.datenStand = this.extractDatenstandFromFileName(fileName);
        if (!job.meta.datenStand) {
            notify('Kein Datenstand ', 'Job konnte nicht gestartet werden', 'info', 10000);
            return;
        }

        if (job.needsBM) {
            if (job.dataType === ImportDataType.googleprofile) {
                // the new way. extract kw from filename in square brackets
                job.meta.kw = extractKWFromFileName(fileName);
            } else {
                job.meta.kw = job.getBmFromFileNameOldWay(job.meta.datenStand);
            }
            if (!job.meta.kw) {
                notify('Kein BM aus Datenstand ', 'Job konnte nicht gestartet werden', 'info', 10000);
                return;
            }
            // if (job.meta.kw < 202500) {
            //     notify('Ungültitger BM ', 'Job konnte nicht gestartet werden', 'info', 10000);
            //     return;
            // }
            job.meta.kw = job.meta.kw.toString();
        }
        if (job.needsKw) {
            if (job.dataType === ImportDataType.leadnowconv) {
                // the new way. extract kw from filename in square brackets
                const kw = extractKWFromFileName(fileName);
                job.meta.kw = kw ? kw.toString() : null;
            } else {
                job.meta.kw = job.getBmFromFileNameOldWay(job.meta.datenStand);
            }
            if (!job.meta.kw) {
                notify('Kein KW aus Datenstand ', 'Job konnte nicht gestartet werden', 'info', 10000);
                return;
            }
        }
        if (job.needsYear) {
            job.meta.kw = job.getYearFromFileName(job.meta.datenStand);
            if (!job.meta.kw) {
                notify('Kein KW aus Datenstand ', 'Job konnte nicht gestartet werden', 'info', 10000);
                return;
            }
        }
        return job;
    }


    @action
    async renameFile(dataType: ImportDataType, orgFilename: string) {
        this.orgFilename = orgFilename;
        this.newFilename = undefined;
        this.uiStore.showModal(this.uiStore.modalIds.adminFileUploadRenamFile);
        while (this.newFilename === undefined) {
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        this.uiStore.hideModal(this.uiStore.modalIds.adminFileUploadRenamFile);
        if (this.newFilename === 'cancel') {
            return;
        }
        const newFileName = this.newFilename;

        return this.adminImportsStore.renameFile(dataType, this.orgFilename, newFileName);

    }


}